import { useState } from 'react';
import { Handshake, Plus } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { ClipLoader } from 'react-spinners';
import { useUser } from '../../../../../utils/UserContext';
import useApi from '../../../../../utils/AxiosWrapper';
import { sanitizeUrl } from '../../../../../utils/SanitizationUtils';
import { AddBrokerModal } from './AddBrokerModal';

interface BrokerContact {
  broker_contact_id: string;
  customer_id: string;
  broker_name: string;
  broker_email?: string;
  broker_phone?: string;
  broker_website?: string;
  created_at: string;
  updated_at: string;
}

function BrokerNetwork() {
  const { userId } = useUser();
  const { callProtectedApi } = useApi();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: brokerContacts, isLoading } = useQuery<BrokerContact[]>({
    queryKey: ['brokerContacts', userId],
    queryFn: async () => {
      const response = await callProtectedApi(
        `/broker-contacts/${userId}`,
        'get',
        {},
        'Error fetching broker contacts.'
      );
      return response?.data?.data?.brokerContacts ?? [];
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div className="flex h-full w-full flex-col items-center overflow-y-auto overflow-x-hidden">
      <div className="flex h-full w-full flex-col sm:w-[95%] md:w-full md:px-4 lg:w-[1148px] lg:max-w-[1148px] lg:px-0">
        {/* Header */}
        <div className="px-4 pb-4 pt-6 sm:px-0">
          <div className="flex items-start justify-between">
            <div className="sm:mr-4">
              <p className="mt-1 text-sm text-gray-500">
                Submit your broker contacts so TACH can help you get more
                freight from your existing network and our broker partners.
              </p>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="hidden shrink-0 items-center gap-2 rounded-lg bg-[#1e3a5f] px-4 py-2 text-sm font-medium text-white hover:bg-[#162d4a] sm:flex"
            >
              <Plus size={16} weight="bold" />
              Add Broker
            </button>
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-[#1e3a5f] px-4 py-2 text-sm font-medium text-white hover:bg-[#162d4a] sm:hidden"
          >
            <Plus size={16} weight="bold" />
            Add Broker
          </button>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <ClipLoader size={32} color="#1e3a5f" />
          </div>
        ) : !brokerContacts || brokerContacts.length === 0 ? (
          /* Empty State */
          <div className="mx-4 flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-12 sm:mx-0 sm:px-6 sm:py-16">
            <Handshake size={48} className="mb-4 text-gray-400" />
            <h3 className="mb-2 text-lg font-semibold text-gray-700">
              No brokers submitted yet
            </h3>
            <p className="mb-6 max-w-md text-center text-sm text-gray-500">
              Add the brokers you work with so TACH can promote you to them and
              help you get more loads.
            </p>
            <button
              onClick={() => setIsModalOpen(true)}
              className="flex items-center gap-2 rounded-lg bg-[#1e3a5f] px-4 py-2 text-sm font-medium text-white hover:bg-[#162d4a]"
            >
              <Plus size={16} weight="bold" />
              Add Your First Broker
            </button>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden overflow-x-auto rounded-xl border border-gray-200 sm:block">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Broker Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Phone
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Website
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Submitted
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {brokerContacts.map((broker) => (
                    <tr
                      key={broker.broker_contact_id}
                      className="hover:bg-gray-50"
                    >
                      <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                        {broker.broker_name}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {broker.broker_email || '-'}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {broker.broker_phone || '-'}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {broker.broker_website ? (
                          <a
                            href={sanitizeUrl(broker.broker_website)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#1e3a5f] hover:underline"
                          >
                            {broker.broker_website}
                          </a>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {new Date(broker.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="flex flex-col gap-3 px-4 sm:hidden">
              {brokerContacts.map((broker) => (
                <div
                  key={broker.broker_contact_id}
                  className="rounded-lg border border-gray-200 bg-white p-4"
                >
                  <p className="text-sm font-semibold text-gray-900">
                    {broker.broker_name}
                  </p>
                  {broker.broker_email && (
                    <p className="mt-1 text-sm text-gray-500">
                      {broker.broker_email}
                    </p>
                  )}
                  {broker.broker_phone && (
                    <p className="mt-1 text-sm text-gray-500">
                      {broker.broker_phone}
                    </p>
                  )}
                  {broker.broker_website && (
                    <a
                      href={sanitizeUrl(broker.broker_website)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 block text-sm text-[#1e3a5f] hover:underline"
                    >
                      {broker.broker_website}
                    </a>
                  )}
                  <p className="mt-2 text-xs text-gray-400">
                    Submitted {new Date(broker.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <AddBrokerModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
}

export default BrokerNetwork;
